import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ListingsPage from "./ListingsPage";
import ProfilePage from "./ProfilePage";
import WorkspaceBookingsPage from "./WorkspaceBookingsPage";
import AddListingsForm from "./AddListingsForm";

interface AccountHostPageProps {
  onToggleMode: () => void;
}

export default function AccountHostPage({
  onToggleMode,
}: AccountHostPageProps) {
  const [searchParams] = useSearchParams();
  const [activeSubpage, setActiveSubpage] = useState("profile");
  const [showAddWorkspace, setShowAddWorkspace] = useState(false);

  // Handle URL parameters to set initial state
  useEffect(() => {
    const section = searchParams.get("section");

    if (section === "addWorkspace") {
      setShowAddWorkspace(true);
      setActiveSubpage("listings");
    } else if (
      section === "listings" ||
      section === "bookings" ||
      section === "profile"
    ) {
      setActiveSubpage(section);
      setShowAddWorkspace(false);
    }
  }, [searchParams]);

  const shouldHideNav = showAddWorkspace;

  function linkClasses(type: string | null = null) {
    let classes =
      "w-full flex items-center gap-4 p-4 rounded-2xl transition-colors text-left cursor-pointer button-hover";
    if (
      type === activeSubpage ||
      (activeSubpage === undefined && type === "profile")
    ) {
      classes += " bg-[var(--secondary)] text-gray-900";
    } else {
      classes += " hover:bg-gray-50 text-gray-700";
    }
    return classes;
  }

  return (
    <div className="flex">
      {/* Left Navigation - 1/6 width */}
      {!shouldHideNav && (
        <div className="w-1/6 min-w-64 bg-white border-r border-gray-200">
          <div className="p-6 sticky top-0">
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {/* Toggle Switch */}
                <div className="flex flex-row items-center justify-between">
                  <button
                    type="button"
                    onClick={onToggleMode}
                    className="
                  px-4 py-2 rounded-md text-sm font-medium text-black transition button-hover"
                  >
                    Switch to experience
                  </button>
                </div>
              </div>
            </div>

            <nav className="space-y-2">
              {/* About me */}
              <button
                className={`${linkClasses("profile")} ${
                  activeSubpage === undefined || activeSubpage === "profile"
                    ? "active"
                    : ""
                } group`}
                onClick={() => setActiveSubpage("profile")}
              >
                <div
                  className={`w-12 h-12 min-w-12 min-h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activeSubpage === undefined || activeSubpage === "profile"
                      ? "bg-white text-black"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                </div>
                <span
                  className={`font-medium ${
                    activeSubpage === undefined || activeSubpage === "profile"
                      ? "text-white"
                      : "text-black"
                  }`}
                >
                  About me
                </span>
              </button>

              {/* Workspace Bookings */}
              <button
                className={`${linkClasses("bookings")} ${
                  activeSubpage === "bookings" ? "active" : ""
                } group`}
                onClick={() => setActiveSubpage("bookings")}
              >
                <div
                  className={`w-12 h-12 min-w-12 min-h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activeSubpage === "bookings"
                      ? "bg-white text-black"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                    />
                  </svg>
                </div>
                <span
                  className={`font-medium ${
                    activeSubpage === undefined || activeSubpage === "bookings"
                      ? "text-white"
                      : "text-black"
                  }`}
                >
                  Workspace Bookings
                </span>
              </button>

              {/* My Workspaces */}
              <button
                className={`${linkClasses("listings")} ${
                  activeSubpage === "listings" ? "active" : ""
                } group`}
                onClick={() => setActiveSubpage("listings")}
              >
                <div
                  className={`w-12 h-12 min-w-12 min-h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activeSubpage === "listings"
                      ? "bg-white text-black"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
                    />
                  </svg>
                </div>
                <span
                  className={`font-medium ${
                    activeSubpage === undefined || activeSubpage === "listings"
                      ? "text-white"
                      : "text-black"
                  }`}
                >
                  My Workspaces
                </span>
              </button>
            </nav>

            {/* Add workspace button - only show when on listings page */}
            {activeSubpage === "listings" && (
              <div className="mt-8">
                <button
                  onClick={() => setShowAddWorkspace(true)}
                  className="w-full flex items-center gap-3 py-3 px-4 text-[var(--secondary-button-50-text)] rounded-2xl hover:bg-[var(--secondary)] hover:text-white transition-colors font-medium"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.5v15m7.5-7.5h-15"
                    />
                  </svg>
                  Add workspace
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right Content Area - 5/6 width */}
      <div className="flex-1">
        <div className="min-h-screen">
          {showAddWorkspace ? (
            <AddListingsForm
              onBack={() => setShowAddWorkspace(false)}
              onSuccess={() => {
                setShowAddWorkspace(false);
                setActiveSubpage("bookings");
              }}
            />
          ) : (
            <>
              {activeSubpage === "profile" && <ProfilePage />}
              {activeSubpage === "listings" && <ListingsPage />}
              {activeSubpage === "bookings" && <WorkspaceBookingsPage />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
